import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import AdminResetPasswordModal from './AdminResetPasswordModal';
import './adminStyles.css';

// Admin API base URL
// Empty-string fallback = relative URLs, works when React is served from the
// same Express origin on Render. Dev: CRA proxy (package.json) handles /api/*.
const API_URL = process.env.REACT_APP_API_URL || '';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { adminLogout, getAuthHeaders, isAdminAuthenticated, adminData } = useAdmin();
  
  // Track if component mounted to prevent duplicate fetches
  const isMounted = useRef(false);
  
  // State management
  const [activeTab, setActiveTab] = useState('reports'); // 'reports', 'flagged-users', 'moderator-requests', 'moderators', 'moderator-actions', 'messages', 'ios-waitlist'
  const [iosWaitlist, setIosWaitlist] = useState([]);
  const [reports, setReports] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [flaggedStats, setFlaggedStats] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Reset password modal state
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  // Communications (admin review)
  const [communicationsModerators, setCommunicationsModerators] = useState([]);
  const [communicationsUsers, setCommunicationsUsers] = useState([]);
  const [communicationsReports, setCommunicationsReports] = useState([]);
  const [communicationsThreadMessages, setCommunicationsThreadMessages] = useState([]);
  const [communicationsSearchModerator, setCommunicationsSearchModerator] = useState('');
  const [communicationsSearchUser, setCommunicationsSearchUser] = useState('');
  const [selectedCommModeratorId, setSelectedCommModeratorId] = useState(null);
  const [selectedCommUserId, setSelectedCommUserId] = useState(null);
  const [selectedCommReportId, setSelectedCommReportId] = useState(null);
  
  // Auth guard - redirect if not authenticated
  useEffect(() => {
    if (!isAdminAuthenticated || !adminData) {
      console.error('Unauthorized access attempt to admin dashboard');
      navigate('/admin/login');
    }
  }, [isAdminAuthenticated, adminData, navigate]);
  
  // Moderator-related state
  const [moderatorRequests, setModeratorRequests] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [moderatorActions, setModeratorActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // eslint-disable-line no-unused-vars
  const [messagesTotalPages, setMessagesTotalPages] = useState(1); // eslint-disable-line no-unused-vars

  const [incidentTypeSettings, setIncidentTypeSettings] = useState([]);
  const [incidentSubtypeSettings, setIncidentSubtypeSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const [reportsConfig, setReportsConfig] = useState(null);
  
  // Filter and search state
  const [filters, setFilters] = useState({
    status: 'active', // 'active', 'deleted', 'hidden', 'all'
    type: 'all',
    search: '',
    page: 1,
    limit: 20
  });
  
  // Selection state for bulk actions
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });

  // Fetch dashboard statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
      }
    }
  };

  const handleResetUserPassword = async (userId, newPassword) => {
    if (!userId) return;
    if (!newPassword || newPassword.trim().length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }

    if (!window.confirm('Reset this user\'s password? This will overwrite their current password.')) {
      return;
    }

    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/reset-password`, { newPassword }, {
        headers: getAuthHeaders()
      });
      alert('Password reset successfully');
      setResetPasswordUser(null);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to reset password');
      }
    }
  };

  // Communications: moderators list
  const fetchCommunicationsModerators = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/communications/moderators`, {
        headers: getAuthHeaders(),
        params: { search: communicationsSearchModerator || '' }
      });
      setCommunicationsModerators(res.data.moderators || []);

      // Reset selections when reloading moderators
      if (!selectedCommModeratorId && (res.data.moderators || []).length > 0) {
        setSelectedCommModeratorId(res.data.moderators[0].moderatorId);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch moderators');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunicationsUsers = async (moderatorId) => {
    if (!moderatorId) {
      setCommunicationsUsers([]);
      setSelectedCommUserId(null);
      setCommunicationsReports([]);
      setSelectedCommReportId(null);
      setCommunicationsThreadMessages([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/communications/moderator/${moderatorId}/users`, {
        headers: getAuthHeaders(),
        params: { search: communicationsSearchUser || '' }
      });
      setCommunicationsUsers(res.data.users || []);
      if (!selectedCommUserId && (res.data.users || []).length > 0) {
        setSelectedCommUserId(res.data.users[0].userId);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunicationsReports = async (moderatorId, userId) => {
    if (!moderatorId || !userId) {
      setCommunicationsReports([]);
      setSelectedCommReportId(null);
      setCommunicationsThreadMessages([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/communications/moderator/${moderatorId}/user/${userId}/reports`, {
        headers: getAuthHeaders()
      });
      setCommunicationsReports(res.data.reports || []);
      if ((res.data.reports || []).length > 0) {
        setSelectedCommReportId(res.data.reports[0].reportId ?? null);
      } else {
        setSelectedCommReportId(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch report threads');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunicationsThread = async (moderatorId, userId, reportId) => {
    if (!moderatorId || !userId) {
      setCommunicationsThreadMessages([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = {};
      if (reportId) params.reportId = reportId;
      const res = await axios.get(`${API_URL}/api/admin/communications/moderator/${moderatorId}/user/${userId}/thread`, {
        headers: getAuthHeaders(),
        params
      });
      setCommunicationsThreadMessages(res.data.messages || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch thread');
    } finally {
      setLoading(false);
    }
  };

  // Fetch reports with filters
  const fetchReports = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        status: filters.status,
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(filters.search && { search: filters.search })
      });

      const response = await axios.get(`${API_URL}/api/admin/reports?${params}`, {
        headers: getAuthHeaders()
      });
      
      setReports(response.data.reports);
      setPagination(response.data.pagination);
      setSelectedReports([]);
      setSelectAll(false);
    } catch (err) {
      // Don't retry on 429 - retrying makes rate limiting worse
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch reports');
      }
      console.error('Error fetching reports:', err);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        adminLogout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch flagged users
  const fetchFlaggedUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: filters.limit
      });

      const response = await axios.get(`${API_URL}/api/admin/flagged-users?${params}`, {
        headers: getAuthHeaders()
      });
      
      setFlaggedUsers(response.data.users);
      setPagination(response.data.pagination);
      setFlaggedStats(response.data.stats);
    } catch (err) {
      // Don't retry on 429 - retrying makes rate limiting worse
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch flagged users');
      }
      console.error('Error fetching flagged users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch moderator requests
  const fetchModeratorRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/admin/moderator-requests`, {
        headers: getAuthHeaders()
      });
      setModeratorRequests(response.data.requests || []);
    } catch (err) {
      // Don't retry on 429 - retrying makes rate limiting worse
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch moderator requests');
      }
      console.error('Error fetching moderator requests:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all moderators
  const fetchModerators = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/admin/moderators`, {
        headers: getAuthHeaders()
      });
      setModerators(response.data.moderators || []);
    } catch (err) {
      // Don't retry on 429 - retrying makes rate limiting worse
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch moderators');
      }
      console.error('Error fetching moderators:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch moderator actions (no retry on 429)
  const fetchModeratorActions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/admin/moderator-actions?limit=50`, {
        headers: getAuthHeaders()
      });
      setModeratorActions(response.data.actions || []);
    } catch (err) {
      // Don't retry on 429 - retrying makes rate limiting worse
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch moderator actions');
      }
      console.error('Error fetching moderator actions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all messages (admin sees everything)
  const fetchAllMessages = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/admin/all-messages?page=${page}&limit=50`, {
        headers: getAuthHeaders()
      });
      setAllMessages(response.data.messages || []);
      setMessagesTotalPages(response.data.totalPages || 1);
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch messages');
      }
      console.error('Error fetching all messages:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data.users || []);
    } catch (err) {
      // Don't retry on 429 - retrying makes rate limiting worse
      if (err.response?.status === 429) {
        setError('Rate limited. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch users');
      }
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle user active status
  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`)) {
      return;
    }
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/toggle`, {}, {
        headers: getAuthHeaders()
      });
      alert(`User ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to update user status');
      }
    }
  };

  // Delete all user reports
  const handleDeleteAllUserReports = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ALL reports from ${userName}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}/reports`, {
        headers: getAuthHeaders()
      });
      alert('All user reports deleted successfully!');
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to delete user reports');
      }
    }
  };

  // Hide all user reports
  const handleHideAllUserReports = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to hide ALL reports from ${userName}?`)) {
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/reports/hide`, {}, {
        headers: getAuthHeaders()
      });
      alert('All user reports hidden successfully!');
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to hide user reports');
      }
    }
  };

  // Handle moderator request approval
  const handleApproveRequest = async (requestId) => {
    try {
      const reqObj = (moderatorRequests || []).find(r => r.id === requestId);
      const defaultRadius = reqObj?.requestedRadiusMeters ? String(reqObj.requestedRadiusMeters) : '5000';
      const approvedRadiusMeters = window.prompt('Approved coverage radius (meters):', defaultRadius);
      if (approvedRadiusMeters === null) {
        return null;
      }

      const response = await axios.post(`${API_URL}/api/admin/moderator-requests/${requestId}/approve`, {
        approvedRadiusMeters: Number(approvedRadiusMeters)
      }, {
        headers: getAuthHeaders()
      });
      alert('Moderator request approved!');
      fetchModeratorRequests();
      return response.data;
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to approve request');
      }
      return null;
    }
  };
  
  // Handle moderator request unapproval (revert to pending)
  const handleUnapproveRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to unapprove this moderator request? The user will lose moderator privileges.')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/moderator-requests/${requestId}/unapprove`, {}, {
        headers: getAuthHeaders()
      });
      alert('Moderator request unapproved! User has been demoted from moderator role.');
      fetchModeratorRequests();
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to unapprove request');
      }
    }
  };

  // Handle moderator request denial
  const handleDenyRequest = async (requestId) => {
    const reason = prompt('Enter reason for denial (optional):');
    try {
      await axios.post(`${API_URL}/api/admin/moderator-requests/${requestId}/deny`, 
        { reason }, 
        { headers: getAuthHeaders() }
      );
      alert('Moderator request denied');
      fetchModeratorRequests();
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to deny request');
      }
    }
  };

  const handleCleanupOrphanedModeratorRequests = async () => {
    if (!window.confirm('Delete orphaned moderator requests (missing user records)?')) {
      return;
    }
    try {
      const res = await axios.delete(`${API_URL}/api/admin/moderator-requests/orphaned`, {
        headers: getAuthHeaders()
      });
      alert(`Deleted ${res.data.deleted || 0} orphaned moderator requests.`);
      fetchModeratorRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete orphaned moderator requests');
    }
  };

  const handleClearAllModeratorRequests = async () => {
    if (!window.confirm('Delete ALL moderator requests? This cannot be undone.')) {
      return;
    }
    try {
      const res = await axios.delete(`${API_URL}/api/admin/moderator-requests`, {
        headers: getAuthHeaders()
      });
      alert(`Deleted ${res.data.deleted || 0} moderator requests.`);
      fetchModeratorRequests();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete moderator requests');
    }
  };

  // Toggle moderator status
  const handleToggleModeratorStatus = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/moderators/${userId}/toggle`, {}, {
        headers: getAuthHeaders()
      });
      alert('Moderator status updated');
      fetchModerators();
    } catch (err) {
      console.error('Toggle moderator error:', err);
      if (err.response?.status === 429) {
        alert('Rate limited. Please wait a moment and try again.');
      } else {
        alert(err.response?.data?.message || 'Failed to update moderator status');
      }
    }
  };

  // Initial load - fetch stats, reports, and moderator requests (staggered to avoid rate limiting)
  useEffect(() => {
    const loadData = async () => {
      await fetchStats();
      await new Promise(resolve => setTimeout(resolve, 300)); // 300ms delay
      await fetchReports();
      await new Promise(resolve => setTimeout(resolve, 300)); // 300ms delay  
      await fetchModeratorRequests();
      isMounted.current = true; // Mark as mounted after initial load
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reports/config`);
        if (!cancelled) setReportsConfig(res.data || null);
      } catch (e) {
        if (!cancelled) setReportsConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const severityFilterOptions = useMemo(() => {
    const rows = reportsConfig?.severityTypes;
    if (Array.isArray(rows) && rows.length) {
      return rows
        .filter(r => r && r.type)
        .slice()
        .sort((a, b) => (Number(a.severityRank) || 0) - (Number(b.severityRank) || 0));
    }
    return [
      { type: 'level_1', severityRank: 1, label: 'Level 1 (Low Severity)' },
      { type: 'level_2', severityRank: 2, label: 'Level 2 (Medium Severity)' },
      { type: 'level_3', severityRank: 3, label: 'Level 3 (High Severity)' }
    ];
  }, [reportsConfig]);

  // Fetch data when tab changes (skip on initial mount - initial load already fetches data)
  useEffect(() => {
    // Skip on initial mount
    if (!isMounted.current) return;
    if (activeTab === 'reports') {
      fetchReports();
      fetchStats();
    } else if (activeTab === 'flagged-users') {
      fetchFlaggedUsers();
    } else if (activeTab === 'moderator-requests') {
      fetchModeratorRequests();
    } else if (activeTab === 'moderators') {
      fetchModerators();
    } else if (activeTab === 'moderator-actions') {
      fetchModeratorActions();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'messages') {
      fetchAllMessages();
    } else if (activeTab === 'communications') {
      fetchCommunicationsModerators();
    } else if (activeTab === 'settings') {
      fetchIncidentTypeSettings();
      fetchIncidentSubtypeSettings();
    } else if (activeTab === 'ios-waitlist') {
      fetchIosWaitlist();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchIosWaitlist = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/ios-waitlist`, { headers: getAuthHeaders() });
      setIosWaitlist(res.data.waitlist || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch iOS waitlist');
    }
  };

  const downloadWaitlistCsv = () => {
    const header = 'Name,Email,Signed Up\n';
    const rows = iosWaitlist.map(r =>
      `"${(r.name || '').replace(/"/g, '""')}","${r.email}","${new Date(r.createdAt).toLocaleString()}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `safeher-ios-waitlist-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const fetchIncidentTypeSettings = async () => {
    setSettingsLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/incident-type-settings`, {
        headers: getAuthHeaders()
      });
      setIncidentTypeSettings(res.data.settings || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchIncidentSubtypeSettings = async () => {
    setSettingsLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/incident-subtype-settings`, {
        headers: getAuthHeaders()
      });
      setIncidentSubtypeSettings(res.data.settings || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch subtype settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveIncidentSubtypeSettings = async () => {
    setSettingsLoading(true);
    setError('');
    try {
      const payload = {
        settings: (incidentSubtypeSettings || []).map(s => ({
          id: s.id,
          severityType: s.severityType,
          subtypeKey: s.subtypeKey,
          label: s.label,
          sortOrder: Number(s.sortOrder) || 0,
          enabled: (s.enabled === 0 || s.enabled === false) ? 0 : 1
        }))
      };

      const res = await axios.put(`${API_URL}/api/admin/incident-subtype-settings`, payload, {
        headers: getAuthHeaders()
      });
      setIncidentSubtypeSettings(res.data.settings || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save subtype settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveIncidentTypeSettings = async () => {
    setSettingsLoading(true);
    setError('');
    try {
      const payload = {
        settings: (incidentTypeSettings || []).map(s => ({
          type: s.type,
          severityRank: Number(s.severityRank) || 0,
          label: s.label ?? null,
          color: s.color ?? null
        }))
      };

      const res = await axios.put(`${API_URL}/api/admin/incident-type-settings`, payload, {
        headers: getAuthHeaders()
      });
      setIncidentTypeSettings(res.data.settings || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Communications: search debounce + selection loading
  useEffect(() => {
    if (activeTab !== 'communications') return;
    const t = setTimeout(() => {
      fetchCommunicationsModerators();
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communicationsSearchModerator]);

  useEffect(() => {
    if (activeTab !== 'communications') return;
    fetchCommunicationsUsers(selectedCommModeratorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommModeratorId]);

  useEffect(() => {
    if (activeTab !== 'communications') return;
    const t = setTimeout(() => {
      fetchCommunicationsUsers(selectedCommModeratorId);
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communicationsSearchUser]);

  useEffect(() => {
    if (activeTab !== 'communications') return;
    fetchCommunicationsReports(selectedCommModeratorId, selectedCommUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommUserId]);

  useEffect(() => {
    if (activeTab !== 'communications') return;
    fetchCommunicationsThread(selectedCommModeratorId, selectedCommUserId, selectedCommReportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommReportId]);

  useEffect(() => {
    if (activeTab !== 'communications') return;
    fetchCommunicationsThread(selectedCommModeratorId, selectedCommUserId, selectedCommReportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommModeratorId, selectedCommUserId]);

  // Fetch reports when filters change (debounced to avoid rate limiting)
  useEffect(() => {
    // Skip on initial mount (initial load already fetches reports)
    if (!isMounted.current) return;
    
    const timer = setTimeout(() => {
      fetchReports();
    }, 200); // 200ms debounce

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
    fetchReports();
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedReports([]);
    } else {
      setSelectedReports(reports.map(r => r.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual selection
  const handleSelectReport = (reportId) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  // Bulk delete (soft delete)
  const handleBulkDelete = async () => {
    if (selectedReports.length === 0) {
      alert('Please select reports to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedReports.length} report(s)?`)) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/reports/bulk-delete`, {
        reportIds: selectedReports,
        permanent: false
      }, {
        headers: getAuthHeaders()
      });

      alert('Reports deleted successfully');
      fetchReports();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete reports');
    }
  };

  // Bulk restore
  const handleBulkRestore = async () => {
    if (selectedReports.length === 0) {
      alert('Please select reports to restore');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/reports/bulk-restore`, {
        reportIds: selectedReports
      }, {
        headers: getAuthHeaders()
      });

      alert('Reports restored successfully');
      fetchReports();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to restore reports');
    }
  };

  // Bulk permanent delete
  const handleBulkPermanentDelete = async () => {
    if (selectedReports.length === 0) {
      alert('Please select reports to permanently delete');
      return;
    }

    if (!window.confirm(`WARNING: This will PERMANENTLY delete ${selectedReports.length} report(s). This action cannot be undone. Are you sure?`)) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/reports/bulk-delete`, {
        reportIds: selectedReports,
        permanent: true
      }, {
        headers: getAuthHeaders()
      });

      alert('Reports permanently deleted');
      fetchReports();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to permanently delete reports');
    }
  };

  // Ban/unban user
  const handleToggleUserBan = async (userId, isBanned) => {
    const action = isBanned ? 'unban' : 'ban';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/flagged-users/${userId}/ban`, {}, {
        headers: getAuthHeaders()
      });

      alert(`User ${action}ned successfully`);
      fetchFlaggedUsers();
    } catch (err) {
      alert(err.response?.data?.message || `Failed to ${action} user`);
    }
  };

  // Clear user violations
  const handleClearViolations = async (userId) => {
    if (!window.confirm('Are you sure you want to clear all violations for this user?')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/flagged-users/${userId}/clear`, {}, {
        headers: getAuthHeaders()
      });

      alert('Violations cleared successfully');
      fetchFlaggedUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to clear violations');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const restoreFileInputRef = useRef(null);

  const uploadAndRestoreDb = async (file) => {
    if (!file) return;
    try {
      const pw = window.prompt('Enter admin password to confirm DB restore:');
      if (!pw) {
        alert('Restore cancelled');
        return;
      }
      const buf = await file.arrayBuffer();

      const res = await axios.post(`${API_URL}/api/admin/db-restore`, buf, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/octet-stream',
          'x-admin-password': pw
        }
      });

      alert(`${res.data?.message || 'Restore completed'}\n\nIf the server doesn’t auto-restart locally, run ./kill-all.sh && ./start-all.sh`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to restore DB');
    } finally {
      if (restoreFileInputRef.current) {
        restoreFileInputRef.current.value = '';
      }
    }
  };

  const downloadDbBackup = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/db-backup`, {
        headers: getAuthHeaders(),
        responseType: 'arraybuffer',
        validateStatus: () => true
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();
      if (res.status >= 400 || contentType.includes('application/json')) {
        try {
          const text = new TextDecoder().decode(res.data);
          const json = JSON.parse(text);
          alert(json?.message || 'Backup failed');
        } catch (e) {
          alert('Backup failed');
        }
        return;
      }

      const contentDisposition = res.headers?.['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] || `safeher-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.db`;

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/x-sqlite3' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to download DB backup');
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>SafeHer Admin Dashboard</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              ref={restoreFileInputRef}
              type="file"
              accept=".db,application/x-sqlite3"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) uploadAndRestoreDb(f);
              }}
            />
            <button
              onClick={() => restoreFileInputRef.current?.click()}
              className="admin-logout-button"
              title="Restore SQLite DB from a .db backup (will restart server)"
            >
              Restore DB
            </button>
            <button
              onClick={downloadDbBackup}
              className="admin-logout-button"
              title="Download SQLite DB backup"
            >
              Download DB
            </button>
            <button onClick={adminLogout} className="admin-logout-button">
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="admin-stats">
          <div className="stat-card">
            <h3>Total Reports</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
          <div className="stat-card">
            <h3>Active Reports</h3>
            <p className="stat-number stat-active">{stats.active}</p>
          </div>
          <div className="stat-card">
            <h3>Deleted Reports</h3>
            <p className="stat-number stat-deleted">{stats.deleted}</p>
          </div>
          <div className="stat-card">
            <h3>Recent (7 days)</h3>
            <p className="stat-number stat-recent">{stats.recentReports}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
        <button 
          className={`tab-button ${activeTab === 'flagged-users' ? 'active' : ''}`}
          onClick={() => setActiveTab('flagged-users')}
        >
          Flagged Users
          {flaggedStats && flaggedStats.totalFlagged > 0 && (
            <span className="tab-badge">{flaggedStats.totalFlagged}</span>
          )}
        </button>
        <button 
          className={`tab-button ${activeTab === 'moderator-requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('moderator-requests')}
        >
          Mod Requests
          {moderatorRequests?.filter(r => r.status === 'pending').length > 0 && (
            <span className="tab-badge">{moderatorRequests.filter(r => r.status === 'pending').length}</span>
          )}
        </button>

        <button
          className="tab-button"
          onClick={() => navigate('/admin/moderator-coverage')}
        >
          Mod Coverage
        </button>
        <button 
          className={`tab-button ${activeTab === 'moderators' ? 'active' : ''}`}
          onClick={() => setActiveTab('moderators')}
        >
          Moderators
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`tab-button ${activeTab === 'moderator-actions' ? 'active' : ''}`}
          onClick={() => setActiveTab('moderator-actions')}
        >
          Mod Actions
        </button>

        <button
          className="tab-button"
          onClick={() => navigate('/admin/area-reports')}
        >
          Area Reports
        </button>

        <button
          className={`tab-button ${activeTab === 'communications' ? 'active' : ''}`}
          onClick={() => setActiveTab('communications')}
        >
          Communications
        </button>

        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={`tab-button ${activeTab === 'ios-waitlist' ? 'active' : ''}`}
          onClick={() => setActiveTab('ios-waitlist')}
          style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
        >
          🍎 iOS Waitlist
        </button>
      </div>

      {activeTab === 'settings' && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '16px' }}>Incident Type Settings</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Configure incident type to severity rank/label/color mapping.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="action-btn" onClick={fetchIncidentTypeSettings} disabled={settingsLoading}>
                Refresh
              </button>
              <button className="action-btn" onClick={saveIncidentTypeSettings} disabled={settingsLoading}>
                Save
              </button>
            </div>
          </div>

          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Severity Rank</th>
                  <th>Label</th>
                  <th>Color</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {settingsLoading && (incidentTypeSettings || []).length === 0 ? (
                  <tr><td colSpan="5" className="no-reports">Loading…</td></tr>
                ) : (incidentTypeSettings || []).length === 0 ? (
                  <tr><td colSpan="5" className="no-reports">No settings found</td></tr>
                ) : (
                  (incidentTypeSettings || []).map((s, idx) => (
                    <tr key={`${s.type || 'row'}-${idx}`}>
                      <td>
                        <input
                          value={s.type || ''}
                          onChange={(e) => {
                            const next = [...(incidentTypeSettings || [])];
                            next[idx] = { ...next[idx], type: e.target.value };
                            setIncidentTypeSettings(next);
                          }}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={s.severityRank ?? 0}
                          onChange={(e) => {
                            const next = [...(incidentTypeSettings || [])];
                            next[idx] = { ...next[idx], severityRank: e.target.value };
                            setIncidentTypeSettings(next);
                          }}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          value={s.label || ''}
                          onChange={(e) => {
                            const next = [...(incidentTypeSettings || [])];
                            next[idx] = { ...next[idx], label: e.target.value };
                            setIncidentTypeSettings(next);
                          }}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            value={s.color || ''}
                            onChange={(e) => {
                              const next = [...(incidentTypeSettings || [])];
                              next[idx] = { ...next[idx], color: e.target.value };
                              setIncidentTypeSettings(next);
                            }}
                            placeholder="#rrggbb"
                            style={{ width: '100%' }}
                          />
                          <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: '1px solid #ddd', background: s.color || '#fff' }} />
                        </div>
                      </td>
                      <td>
                        <button
                          className="action-btn"
                          onClick={() => {
                            const next = [...(incidentTypeSettings || [])];
                            next.splice(idx, 1);
                            setIncidentTypeSettings(next);
                          }}
                          title="Remove row"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '10px' }}>
            <button
              className="action-btn"
              onClick={() => setIncidentTypeSettings(prev => ([...(prev || []), { type: '', severityRank: 0, label: '', color: '' }]))}
            >
              Add Row
            </button>
          </div>

          <div style={{ height: '18px' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '16px' }}>Incident Subtype Catalog</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Configure the Incident Type dropdown options (per severity level).
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="action-btn" onClick={fetchIncidentSubtypeSettings} disabled={settingsLoading}>
                Refresh
              </button>
              <button className="action-btn" onClick={saveIncidentSubtypeSettings} disabled={settingsLoading}>
                Save
              </button>
            </div>
          </div>

          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Key</th>
                  <th>Label</th>
                  <th>Order</th>
                  <th>Enabled</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {settingsLoading && (incidentSubtypeSettings || []).length === 0 ? (
                  <tr><td colSpan="6" className="no-reports">Loading…</td></tr>
                ) : (incidentSubtypeSettings || []).length === 0 ? (
                  <tr><td colSpan="6" className="no-reports">No subtype settings found</td></tr>
                ) : (
                  (incidentSubtypeSettings || []).map((s, idx) => (
                    <tr key={`${s.id || s.subtypeKey || 'row'}-${idx}`}>
                      <td>
                        <select
                          value={s.severityType || 'level_1'}
                          onChange={(e) => {
                            const next = [...(incidentSubtypeSettings || [])];
                            next[idx] = { ...next[idx], severityType: e.target.value };
                            setIncidentSubtypeSettings(next);
                          }}
                        >
                          <option value="level_1">Level 1</option>
                          <option value="level_2">Level 2</option>
                          <option value="level_3">Level 3</option>
                        </select>
                      </td>
                      <td>
                        <input
                          value={s.subtypeKey || ''}
                          onChange={(e) => {
                            const next = [...(incidentSubtypeSettings || [])];
                            next[idx] = { ...next[idx], subtypeKey: e.target.value };
                            setIncidentSubtypeSettings(next);
                          }}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          value={s.label || ''}
                          onChange={(e) => {
                            const next = [...(incidentSubtypeSettings || [])];
                            next[idx] = { ...next[idx], label: e.target.value };
                            setIncidentSubtypeSettings(next);
                          }}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={s.sortOrder ?? 0}
                          onChange={(e) => {
                            const next = [...(incidentSubtypeSettings || [])];
                            next[idx] = { ...next[idx], sortOrder: e.target.value };
                            setIncidentSubtypeSettings(next);
                          }}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!(s.enabled === 0 || s.enabled === false)}
                          onChange={(e) => {
                            const next = [...(incidentSubtypeSettings || [])];
                            next[idx] = { ...next[idx], enabled: e.target.checked ? 1 : 0 };
                            setIncidentSubtypeSettings(next);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          className="action-btn"
                          onClick={() => {
                            const next = [...(incidentSubtypeSettings || [])];
                            next.splice(idx, 1);
                            setIncidentSubtypeSettings(next);
                          }}
                          title="Remove row"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '10px' }}>
            <button
              className="action-btn"
              onClick={() => setIncidentSubtypeSettings(prev => ([...(prev || []), { severityType: 'level_1', subtypeKey: '', label: '', sortOrder: 0, enabled: 1 }]))}
            >
              Add Subtype
            </button>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <>
      {/* Filters and Search */}
      <div className="admin-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Reports</option>
              <option value="active">Active Only</option>
              <option value="deleted">Deleted Only</option>
              <option value="hidden">Hidden Only</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Severity Level:</label>
            <select 
              value={filters.type} 
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="all">All Levels</option>
              {severityFilterOptions.map(opt => (
                <option key={opt.type} value={opt.type}>
                  {opt.label || opt.type}
                </option>
              ))}
            </select>
          </div>

          <form onSubmit={handleSearch} className="filter-group search-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search description, user, type..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
            <button type="submit">Search</button>
          </form>
        </div>

        {/* Bulk Actions */}
        {selectedReports.length > 0 && (
          <div className="bulk-actions">
            <span className="bulk-selection-count">
              {selectedReports.length} selected
            </span>
            {filters.status !== 'deleted' && (
              <button onClick={handleBulkDelete} className="bulk-action-btn delete-btn">
                Delete Selected
              </button>
            )}
            {filters.status === 'deleted' && (
              <>
                <button onClick={handleBulkRestore} className="bulk-action-btn restore-btn">
                  Restore Selected
                </button>
                <button onClick={handleBulkPermanentDelete} className="bulk-action-btn permanent-delete-btn">
                  Permanent Delete
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Reports Table */}
      <div className="admin-content">
        {error && <div className="error-message">{error}</div>}
        
        {loading ? (
          <div className="loading-spinner">Loading reports...</div>
        ) : (
          <>
            <div className="reports-table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>User</th>
                    <th>Address</th>
                    <th>Upvotes</th>
                    <th>Comments</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(reports || []).length === 0 ? (
                    <tr>
                      <td colSpan="10" className="no-reports">
                        No reports found
                      </td>
                    </tr>
                  ) : (
                    (reports || []).map(report => (
                      <tr 
                        key={report.id}
                        className={report.isDeleted ? 'deleted-row' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedReports.includes(report.id)}
                            onChange={() => handleSelectReport(report.id)}
                          />
                        </td>
                        <td className="report-id">{report.id.slice(0, 8)}...</td>
                        <td>
                          <span className={`type-badge type-${report.type}`}>
                            {report.type}
                          </span>
                        </td>
                        <td className="report-description">
                          {report.description?.substring(0, 100)}
                          {report.description?.length > 100 && '...'}
                        </td>
                        <td>
                          {report.creatorName || report.creatorEmail
                            ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {report.creatorName ? (
                                  <div style={{ fontWeight: 600, color: '#333' }}>{report.creatorName}</div>
                                ) : null}
                                {report.creatorEmail ? (
                                  <div style={{ fontSize: '12px', color: '#6B8E6F' }}>{report.creatorEmail}</div>
                                ) : null}
                              </div>
                            )
                            : (report.user || 'Anonymous')}
                        </td>
                        <td className="report-location">
                          {report.address ? (
                            <>
                              <div style={{ marginBottom: '4px', color: '#333', fontWeight: '500' }}>
                                {report.address}
                              </div>
                              <a 
                                href={`https://www.google.com/maps?q=${report.position?.lat},${report.position?.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  fontSize: '11px', 
                                  color: '#6B8E6F',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {report.position?.lat.toFixed(4)}, {report.position?.lng.toFixed(4)}
                              </a>
                            </>
                          ) : (
                            <a 
                              href={`https://www.google.com/maps?q=${report.position?.lat},${report.position?.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#6B8E6F',
                                textDecoration: 'none'
                              }}
                            >
                              {report.position?.lat.toFixed(4)}, {report.position?.lng.toFixed(4)}
                            </a>
                          )}
                        </td>
                        <td>{report.upvoteCount || 0}</td>
                        <td>{report.comments?.length || 0}</td>
                        <td className="report-date">{formatDate(report.timestamp)}</td>
                        <td>
                          {report.isDeleted ? (
                            <span className="status-badge status-deleted">Deleted</span>
                          ) : report.hiddenByModerator ? (
                            <span className="status-badge status-hidden">Hidden</span>
                          ) : (
                            <span className="status-badge status-active">Active</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination?.page - 1)}
                  disabled={pagination?.page === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination?.page || 1} of {pagination?.totalPages || 1} 
                  ({pagination?.total || 0} total reports)
                </span>
                <button
                  onClick={() => handlePageChange(pagination?.page + 1)}
                  disabled={pagination?.page === pagination?.totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}

      {/* Flagged Users Tab */}
      {activeTab === 'flagged-users' && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading-spinner">Loading flagged users...</div>
          ) : (
            <>
              <div className="reports-table-container">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Total Violations</th>
                      <th>Last Violation</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(flaggedUsers || []).length === 0 ? (
                      <tr>
                        <td colSpan="7" className="no-reports">
                          No flagged users found
                        </td>
                      </tr>
                    ) : (
                      (flaggedUsers || []).map(user => (
                        <tr key={user.userId} className={user.isBanned ? 'banned-row' : ''}>
                          <td className="report-id">{user.userId.slice(0, 8)}...</td>
                          <td>{user.userName}</td>
                          <td>{user.userEmail}</td>
                          <td>
                            <span className={`violation-count ${user.totalViolations >= 5 ? 'high' : user.totalViolations >= 3 ? 'medium' : 'low'}`}>
                              {user.totalViolations}
                            </span>
                          </td>
                          <td className="report-date">{formatDate(user.lastViolation)}</td>
                          <td>
                            {user.isBanned ? (
                              <span className="status-badge status-banned">Banned</span>
                            ) : (
                              <span className="status-badge status-active">Active</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleToggleUserBan(user.userId, user.isBanned)}
                                className={user.isBanned ? 'bulk-action-btn restore-btn' : 'bulk-action-btn delete-btn'}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                              >
                                {user.isBanned ? 'Unban' : 'Ban'}
                              </button>
                              <button
                                onClick={() => handleClearViolations(user.userId)}
                                className="bulk-action-btn"
                                style={{ fontSize: '12px', padding: '4px 8px', background: '#6c757d', color: 'white' }}
                              >
                                Clear
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(pagination?.page - 1)}
                    disabled={pagination?.page === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination?.page || 1} of {pagination?.totalPages || 1} 
                    ({pagination?.total || 0} total flagged users)
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination?.page + 1)}
                    disabled={pagination?.page === pagination?.totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Moderator Requests Tab */}
      {activeTab === 'moderator-requests' && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
            <button className="btn-warning" onClick={handleCleanupOrphanedModeratorRequests}>
              Clear Orphaned Requests
            </button>
            <button className="btn-deny" onClick={handleClearAllModeratorRequests}>
              Clear ALL Requests
            </button>
          </div>
          
          {loading ? (
            <div className="loading-spinner">Loading moderator requests...</div>
          ) : (
            <div className="moderator-requests-grid">
              {(moderatorRequests || []).length === 0 ? (
                <div className="no-reports">No moderator requests found</div>
              ) : (
                (moderatorRequests || []).map(request => (
                  <div key={request.id} className="moderator-request-card">
                    <div className="request-header">
                      <h3>{request.userName}</h3>
                      <span className={`request-status ${request.status}`}>{request.status}</span>
                    </div>
                    <div className="request-details">
                      <p><strong>Contact:</strong> {request.contactInfo}</p>
                      <p><strong>Zipcode:</strong> {request.zipcode}</p>
                      {(request.chapter || request.city || request.requestedRadiusMeters) && (
                        <>
                          {request.chapter && <p><strong>Chapter:</strong> {request.chapter}</p>}
                          {request.city && <p><strong>City:</strong> {request.city}</p>}
                          {request.requestedRadiusMeters && <p><strong>Requested radius:</strong> {request.requestedRadiusMeters} m</p>}
                          {(typeof request.requestedCenterLat === 'number' || typeof request.requestedCenterLat === 'string') &&
                            (typeof request.requestedCenterLng === 'number' || typeof request.requestedCenterLng === 'string') && (
                            <p>
                              <strong>Requested center:</strong>{' '}
                              <a
                                href={`https://www.google.com/maps?q=${encodeURIComponent(`${Number(request.requestedCenterLat).toFixed(6)},${Number(request.requestedCenterLng).toFixed(6)}`)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {Number(request.requestedCenterLat).toFixed(5)}, {Number(request.requestedCenterLng).toFixed(5)}
                              </a>
                            </p>
                          )}
                          {request.approvedRadiusMeters && <p><strong>Approved radius:</strong> {request.approvedRadiusMeters} m</p>}
                        </>
                      )}
                      <p><strong>Experience:</strong></p>
                      <p className="request-experience">{request.experience}</p>
                      <p className="request-date">
                        Submitted: {new Date(request.createdAt).toLocaleString()}
                      </p>
                    </div>
                    {request.status === 'pending' && (
                      <div className="request-actions">
                        <button 
                          onClick={() => handleApproveRequest(request.id)}
                          className="btn-approve"
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => handleDenyRequest(request.id)}
                          className="btn-deny"
                        >
                          Deny
                        </button>
                      </div>
                    )}
                    {request.status === 'approved' && (
                      <div className="request-actions">
                        <button 
                          onClick={() => handleUnapproveRequest(request.id)}
                          className="btn-warning"
                        >
                          Unapprove
                        </button>
                        <button 
                          onClick={() => navigate('/moderator/dashboard')}
                          className="btn-info"
                        >
                          View Moderator Dashboard
                        </button>
                      </div>
                    )}
                    {request.status === 'denied' && request.reviewNotes && (
                      <p className="denial-reason"><strong>Reason:</strong> {request.reviewNotes}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Moderators Tab */}
      {activeTab === 'moderators' && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading-spinner">Loading moderators...</div>
          ) : (
            <div className="reports-table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Promoted On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(moderators || []).length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-reports">No moderators found</td>
                    </tr>
                  ) : (
                    (moderators || []).map(mod => (
                      <tr key={mod.id}>
                        <td>
                          {mod.name || `${mod.firstName || ''} ${mod.lastName || ''}`.trim() || 'N/A'}
                        </td>
                        <td>{mod.email}</td>
                        <td>
                          <span
                            className={`status-badge ${(mod.moderatorActive === true || mod.moderatorActive === 1) ? 'active' : 'inactive'}`}
                          >
                            {(mod.moderatorActive === true || mod.moderatorActive === 1) ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          {mod.promotedAt
                            ? new Date(mod.promotedAt).toLocaleString()
                            : (mod.moderatorSince
                              ? new Date(mod.moderatorSince).toLocaleString()
                              : (mod.roleUpdatedAt
                                ? new Date(mod.roleUpdatedAt).toLocaleString()
                                : 'N/A'))}
                        </td>
                        <td>
                          <button 
                            onClick={() => handleToggleModeratorStatus(mod.id)}
                            className="action-btn"
                          >
                            {(mod.moderatorActive === true || mod.moderatorActive === 1) ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Moderator Actions Tab */}
      {activeTab === 'moderator-actions' && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading-spinner">Loading moderator actions...</div>
          ) : (
            <div className="reports-table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Moderator</th>
                    <th>Action Type</th>
                    <th>Target User</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(moderatorActions || []).length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-reports">No moderator actions found</td>
                    </tr>
                  ) : (
                    (moderatorActions || []).map(action => (
                      <tr key={action.id}>
                        <td>{new Date(action.timestamp).toLocaleString()}</td>
                        <td>{action.moderatorName}</td>
                        <td>
                          <span className="action-type">{action.actionType.replace(/_/g, ' ').toUpperCase()}</span>
                        </td>
                        <td>{action.details.targetUserName || 'N/A'}</td>
                        <td className="action-details">
                          {action.details.reason && <p><strong>Reason:</strong> {action.details.reason}</p>}
                          {action.details.customMessage && <p><strong>Message:</strong> {action.details.customMessage}</p>}
                          {action.details.postId && <p><strong>Post ID:</strong> {action.details.postId}</p>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Users Management Tab */}
      {activeTab === 'users' && (
        <div className="admin-content">
          <h3 style={{ marginBottom: '20px' }}>User Management</h3>
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading-spinner">Loading users...</div>
          ) : (
            <div className="reports-table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Reports Count</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(users || []).length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-reports">No users found</td>
                    </tr>
                  ) : (
                    (users || []).map(user => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.reportCount || 0}</td>
                        <td>
                          <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                              className="action-btn"
                              style={{
                                backgroundColor: user.isActive ? '#ffc107' : '#28a745',
                                padding: '6px 12px',
                                fontSize: '12px'
                              }}
                            >
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>

                            <button
                              onClick={() => setResetPasswordUser(user)}
                              className="action-btn"
                              style={{
                                backgroundColor: '#2C341B',
                                padding: '6px 12px',
                                fontSize: '12px'
                              }}
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleHideAllUserReports(user.id, user.name)}
                              className="action-btn"
                              style={{
                                backgroundColor: '#6c757d',
                                padding: '6px 12px',
                                fontSize: '12px'
                              }}
                              disabled={!user.reportCount}
                            >
                              Hide Reports
                            </button>
                            <button
                              onClick={() => handleDeleteAllUserReports(user.id, user.name)}
                              className="action-btn"
                              style={{
                                backgroundColor: '#a0715f',
                                padding: '6px 12px',
                                fontSize: '12px'
                              }}
                              disabled={!user.reportCount}
                            >
                              Delete Reports
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Communications Tab */}
      {activeTab === 'communications' && (
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '260px 320px 1fr', gap: '12px', alignItems: 'stretch' }}>
            {/* Left: moderators */}
            <div className="reports-table-container" style={{ padding: '12px', borderRadius: '10px' }}>
              <div style={{ fontWeight: 800, marginBottom: '10px' }}>Moderators</div>
              <input
                type="text"
                placeholder="Search moderators..."
                value={communicationsSearchModerator}
                onChange={(e) => setCommunicationsSearchModerator(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '10px' }}
              />
              {loading && communicationsModerators.length === 0 ? (
                <div className="loading-spinner">Loading...</div>
              ) : communicationsModerators.length === 0 ? (
                <div className="no-reports">No moderators found</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '65vh', overflowY: 'auto' }}>
                  {communicationsModerators.map(m => (
                    <button
                      key={m.moderatorId}
                      onClick={() => {
                        setSelectedCommModeratorId(m.moderatorId);
                        setSelectedCommUserId(null);
                        setSelectedCommReportId(null);
                      }}
                      className="action-btn"
                      style={{
                        textAlign: 'left',
                        background: selectedCommModeratorId === m.moderatorId ? '#f3f7ef' : '#ffffff',
                        color: '#333',
                        border: selectedCommModeratorId === m.moderatorId ? '1px solid #97AC82' : '1px solid #eee',
                        borderRadius: '10px',
                        padding: '10px 10px'
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{m.moderatorName || m.moderatorEmail || m.moderatorId}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{m.moderatorEmail || ''}</div>
                      <div style={{ fontSize: '12px', color: '#777' }}>
                        {m.lastTimestamp ? new Date(m.lastTimestamp).toLocaleString() : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Middle: users */}
            <div className="reports-table-container" style={{ padding: '12px', borderRadius: '10px' }}>
              <div style={{ fontWeight: 800, marginBottom: '10px' }}>Users</div>
              <input
                type="text"
                placeholder="Search users..."
                value={communicationsSearchUser}
                onChange={(e) => setCommunicationsSearchUser(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '10px' }}
                disabled={!selectedCommModeratorId}
              />

              {!selectedCommModeratorId ? (
                <div className="no-reports">Select a moderator</div>
              ) : loading && communicationsUsers.length === 0 ? (
                <div className="loading-spinner">Loading...</div>
              ) : communicationsUsers.length === 0 ? (
                <div className="no-reports">No users found</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '65vh', overflowY: 'auto' }}>
                  {communicationsUsers.map(u => (
                    <button
                      key={u.userId}
                      onClick={() => {
                        setSelectedCommUserId(u.userId);
                        setSelectedCommReportId(null);
                      }}
                      className="action-btn"
                      style={{
                        textAlign: 'left',
                        background: selectedCommUserId === u.userId ? '#f3f7ef' : '#ffffff',
                        color: '#333',
                        border: selectedCommUserId === u.userId ? '1px solid #97AC82' : '1px solid #eee',
                        borderRadius: '10px',
                        padding: '10px 10px'
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{u.userEmail || `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() || u.userId}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{u.userId}</div>
                      <div style={{ fontSize: '12px', color: '#777' }}>
                        {u.lastTimestamp ? new Date(u.lastTimestamp).toLocaleString() : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: report threads + messages */}
            <div className="reports-table-container" style={{ padding: '12px', borderRadius: '10px', display: 'flex', flexDirection: 'column', minHeight: '65vh' }}>
              <div style={{ fontWeight: 800, marginBottom: '8px' }}>Conversation</div>

              {!selectedCommModeratorId || !selectedCommUserId ? (
                <div className="no-reports">Select a moderator and user</div>
              ) : (
                <>
                  {/* Report thread selector */}
                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '10px' }}>
                    {(communicationsReports || []).map(r => {
                      const rid = r.reportId ?? null;
                      const active = (selectedCommReportId ?? null) === rid;
                      const label = rid
                        ? `${r.reportType || 'Report'}${r.reportDescription ? ` - ${r.reportDescription.substring(0, 28)}${r.reportDescription.length > 28 ? '...' : ''}` : ''}`
                        : 'General';
                      return (
                        <button
                          key={rid || 'general'}
                          onClick={() => setSelectedCommReportId(rid)}
                          className="action-btn"
                          style={{
                            whiteSpace: 'nowrap',
                            background: active ? '#97AC82' : '#f8f9fa',
                            color: active ? 'white' : '#333',
                            borderRadius: '999px',
                            padding: '6px 10px',
                            fontSize: '12px'
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Messages */}
                  <div style={{ flex: 1, overflowY: 'auto', paddingRight: '6px' }}>
                    {loading && communicationsThreadMessages.length === 0 ? (
                      <div className="loading-spinner">Loading...</div>
                    ) : communicationsThreadMessages.length === 0 ? (
                      <div className="no-reports">No messages in this thread</div>
                    ) : (
                      communicationsThreadMessages.map(m => (
                        <div key={m.id} style={{ border: '1px solid #eee', borderRadius: '10px', padding: '10px 12px', marginBottom: '10px', background: 'white' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
                            <div style={{ fontWeight: 800, fontSize: '12px' }}>Moderator</div>
                            <div style={{ fontSize: '12px', color: '#777' }}>{new Date(m.timestamp).toLocaleString()}</div>
                          </div>
                          <div style={{ fontSize: '13px' }}>{m.message}</div>
                          {m.userResponse && (
                            <div style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '10px', background: '#f7f7f7', border: '1px solid #e9e9e9' }}>
                              <div style={{ fontWeight: 800, fontSize: '12px', marginBottom: '4px' }}>User response</div>
                              <div style={{ fontSize: '13px' }}>{m.userResponse}</div>
                              {m.respondedAt && (
                                <div style={{ fontSize: '12px', color: '#777', marginTop: '6px' }}>
                                  Responded at: {new Date(m.respondedAt).toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'ios-waitlist' && (
        <div className="admin-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '18px', color: '#004D40' }}>🍎 iOS Waitlist</div>
              <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                {iosWaitlist.length} user{iosWaitlist.length !== 1 ? 's' : ''} waiting for iOS TestFlight access
              </div>
            </div>
            {iosWaitlist.length > 0 && (
              <button
                onClick={downloadWaitlistCsv}
                style={{
                  background: '#00897B', color: 'white', border: 'none',
                  padding: '9px 18px', borderRadius: '8px', fontWeight: 600,
                  fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                ⬇ Download CSV
              </button>
            )}
          </div>

          {iosWaitlist.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🍎</div>
              <p style={{ fontSize: '15px' }}>No iOS waitlist signups yet.</p>
              <p style={{ fontSize: '13px', marginTop: '6px' }}>
                Share the landing page to start collecting emails.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#E0F2F1' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', color: '#004D40', fontWeight: 700, borderBottom: '2px solid #B2DFDB' }}>#</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', color: '#004D40', fontWeight: 700, borderBottom: '2px solid #B2DFDB' }}>Name</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', color: '#004D40', fontWeight: 700, borderBottom: '2px solid #B2DFDB' }}>Email</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', color: '#004D40', fontWeight: 700, borderBottom: '2px solid #B2DFDB' }}>Signed Up</th>
                  </tr>
                </thead>
                <tbody>
                  {iosWaitlist.map((row, i) => (
                    <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafb', transition: 'background 0.15s' }}>
                      <td style={{ padding: '10px 14px', color: '#888', borderBottom: '1px solid #eee' }}>{i + 1}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 500, borderBottom: '1px solid #eee' }}>{row.name || <span style={{ color: '#bbb' }}>—</span>}</td>
                      <td style={{ padding: '10px 14px', color: '#00897B', borderBottom: '1px solid #eee' }}>
                        <a href={`mailto:${row.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{row.email}</a>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#666', fontSize: '13px', borderBottom: '1px solid #eee' }}>
                        {new Date(row.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {resetPasswordUser && (
        <AdminResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onConfirm={(newPassword) => handleResetUserPassword(resetPasswordUser.id, newPassword)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
